// Localização: frontend/src/components/Prontuario/ProntuarioTabs.jsx (versão 0.5.0)
// Refatorado para melhor responsividade mobile com estrutura matrix
// Adicionado controle de permissões RBAC por aba

import React, { useState, useMemo, useEffect } from 'react';
import { usePermissions } from '../../context/PermissionContext';
import Alert from '../UI/Alert';
import { 
    ChevronDown, 
    Leaf,
    ClipboardList,
    Pill,
    Stethoscope,
    CalendarCheck,
    CheckCircle2,
    Clock,
    FileText,
    FileEdit,
    DollarSign,
    UserCheck,
    FolderOpen
} from 'lucide-react';

// Importar as abas
import PacienteOverviewTab from './PacienteOverviewTab'; 
import PacienteHistoricoCuidadosTab from './PacienteHistoricoCuidadosTab';
import PacienteAgendamentosTab from './PacienteAgendamentosTab';
import PacienteTarefasConcluidasTab from './PacienteTarefasConcluidasTab';
import PacienteMedicacaoTab from './PacienteMedicacaoTab'; 
import PacienteEquipamentosTab from './PacienteEquipamentosTab'; 
import PacienteTimelineTab from './PacienteTimelineTab';
import PacienteHistoricoMedicoTab from './PacienteHistoricoMedicoTab';
import NotasClinicasTab from './NotasClinicasTab';
import PacienteDocumentacaoTab from './PacienteDocumentacaoTab';
import PacienteContasReceberTab from './PacienteContasReceberTab';
import PacienteResponsavelTab from './PacienteResponsavelTab';

/**
 * Matrix de configuração das tabs do prontuário
 * Estrutura organizada com metadados para melhor gerenciamento
 * 
 * requiredResource: Recurso RBAC necessário para acessar a aba
 * Se null ou undefined, a aba é sempre acessível (apenas requer acesso ao prontuário)
 */
const TABS_MATRIX = [
    { 
        name: 'Visão Geral', 
        shortName: 'Visão',
        id: 'overview', 
        component: PacienteOverviewTab,
        icon: Leaf,
        priority: 1,
        category: 'principal',
        requiredResource: 'prontuario' // Requer acesso ao prontuário
    },
    { 
        name: 'Timeline', 
        shortName: 'Timeline',
        id: 'timeline', 
        component: PacienteTimelineTab,
        icon: Clock,
        priority: 2,
        category: 'principal',
        requiredResource: 'prontuario'
    },
    { 
        name: 'Histórico de Cuidados', 
        shortName: 'Histórico',
        id: 'historico', 
        component: PacienteHistoricoCuidadosTab,
        icon: ClipboardList,
        priority: 3,
        category: 'principal',
        requiredResource: 'diario' // Histórico de cuidados está relacionado ao diário
    },
    { 
        name: 'Medicações', 
        shortName: 'Medicações',
        id: 'medicacoes', 
        component: PacienteMedicacaoTab,
        icon: Pill,
        priority: 4,
        category: 'tratamento',
        requiredResource: 'planos_medicos'
    },
    { 
        name: 'Histórico Médico', 
        shortName: 'Hist. Médico',
        id: 'historico-medico', 
        component: PacienteHistoricoMedicoTab,
        icon: FileText,
        priority: 4.5,
        category: 'tratamento',
        requiredResource: 'prontuario'
    },
    { 
        name: 'Notas Clínicas', 
        shortName: 'Notas',
        id: 'notas-clinicas', 
        component: NotasClinicasTab,
        icon: FileEdit,
        priority: 4.6,
        category: 'tratamento',
        requiredResource: 'prontuario'
    },
    { 
        name: 'Documentação', 
        shortName: 'Docs',
        id: 'documentacao', 
        component: PacienteDocumentacaoTab,
        icon: FolderOpen,
        priority: 4.7,
        category: 'tratamento',
        requiredResource: 'documentos_paciente'
    },
    { 
        name: 'Equipamentos', 
        shortName: 'Equipamentos',
        id: 'equipamentos', 
        component: PacienteEquipamentosTab,
        icon: Stethoscope,
        priority: 5,
        category: 'tratamento',
        requiredResource: 'equipamentos'
    },
    { 
        name: 'Agendamentos', 
        shortName: 'Agendamentos',
        id: 'agendamentos', 
        component: PacienteAgendamentosTab,
        icon: CalendarCheck,
        priority: 6,
        category: 'agenda',
        requiredResource: 'agendamentos'
    },
    { 
        name: 'Tarefas Concluídas', 
        shortName: 'Concluídas',
        id: 'tarefas-concluidas', 
        component: PacienteTarefasConcluidasTab,
        icon: CheckCircle2,
        priority: 7,
        category: 'agenda',
        requiredResource: 'agenda_tarefas'
    },
    { 
        name: 'Contas a Receber', 
        shortName: 'Financeiro',
        id: 'contas-receber', 
        component: PacienteContasReceberTab,
        icon: DollarSign,
        priority: 8,
        category: 'administrativo',
        requiredResource: 'financeiro'
    },
    { 
        name: 'Responsável', 
        shortName: 'Responsável',
        id: 'responsavel', 
        component: PacienteResponsavelTab,
        icon: UserCheck,
        priority: 9,
        category: 'administrativo',
        requiredResource: 'pacientes' // Responsável é parte da gestão de pacientes
    },
];

/**
 * Componente de navegação por tabs responsivo
 * Desktop: Tabs exibidas em duas linhas para evitar scroll horizontal
 * Mobile: Dropdown select para melhor usabilidade
 * 
 * ✅ NOVO: Filtra abas baseado nas permissões RBAC do usuário
 */
const ProntuarioTabs = ({ pacienteId }) => {
    const { checkPermission } = usePermissions();
    const [currentTab, setCurrentTab] = useState(null);
    const [showMobileMenu, setShowMobileMenu] = useState(false);

    // Filtrar abas baseado nas permissões do usuário
    const availableTabs = useMemo(() => {
        return TABS_MATRIX.filter(tab => {
            // Se não tem requiredResource, sempre disponível (apenas requer prontuário)
            if (!tab.requiredResource) {
                return checkPermission('prontuario', 'can_read');
            }
            // Verificar permissão de leitura no recurso específico
            return checkPermission(tab.requiredResource, 'can_read');
        });
    }, [checkPermission]);

    // Definir aba inicial (primeira aba disponível) ou ajustar se a atual não estiver mais disponível
    useEffect(() => {
        if (availableTabs.length > 0) {
            // Se não há aba selecionada, selecionar a primeira disponível
            if (!currentTab) {
                setCurrentTab(availableTabs[0].id);
            } else {
                // Se a aba atual não está mais disponível, mudar para a primeira disponível
                const currentTabData = availableTabs.find(tab => tab.id === currentTab);
                if (!currentTabData) {
                    setCurrentTab(availableTabs[0].id);
                }
            }
        }
    }, [availableTabs, currentTab]);

    const ActiveComponent = availableTabs.find(tab => tab.id === currentTab)?.component;
    const currentTabData = availableTabs.find(tab => tab.id === currentTab);
    const CurrentTabIcon = currentTabData?.icon;

    const handleTabChange = (tabId) => {
        setCurrentTab(tabId);
        setShowMobileMenu(false);
    };

    // Se não há abas disponíveis, mostrar mensagem
    if (availableTabs.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden w-full p-6">
                <Alert 
                    type="warning" 
                    message="Você não tem permissão para visualizar nenhuma aba do prontuário. Entre em contato com o administrador." 
                />
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden w-full">
            {/* Navegação Mobile: Dropdown Select */}
            <div className="lg:hidden border-b border-gray-200">
                <button
                    onClick={() => setShowMobileMenu(!showMobileMenu)}
                    className="w-full px-4 py-3 flex items-center justify-between text-left bg-white hover:bg-gray-50 transition-colors"
                >
                    <div className="flex items-center gap-2">
                        {CurrentTabIcon && <CurrentTabIcon className="w-6 h-6 text-emerald-600" />}
                        <span className="text-base font-semibold text-emerald-600">{currentTabData?.name}</span>
                    </div>
                    <ChevronDown 
                        className={`w-6 h-6 text-gray-500 transition-transform ${showMobileMenu ? 'rotate-180' : ''}`} 
                    />
                </button>
                
                {showMobileMenu && (
                    <div className="border-t border-gray-200 bg-gray-50 max-h-64 overflow-y-auto">
                        {availableTabs.map(tab => {
                            const IconComponent = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => handleTabChange(tab.id)}
                                    className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-colors
                                        ${currentTab === tab.id
                                            ? 'bg-emerald-50 text-emerald-700 font-semibold border-l-4 border-emerald-500'
                                            : 'hover:bg-gray-100 text-gray-700'
                                        }`}
                                >
                                    <IconComponent className="w-6 h-6 flex-shrink-0" />
                                    <span className="text-base">{tab.name}</span>
                                    {currentTab === tab.id && (
                                        <CheckCircle2 className="ml-auto w-6 h-6 text-emerald-600" />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Navegação Desktop: Tabs em Duas Linhas */}
            <div className="hidden lg:block border-b border-gray-200">
                <nav
                    className="flex flex-wrap gap-x-3 gap-y-1 px-4 py-2"
                    aria-label="Tabs"
                >
                    {availableTabs.map(tab => {
                        const IconComponent = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                data-tab-id={tab.id}
                                onClick={() => handleTabChange(tab.id)}
                                className={`
                                    px-4 py-3 text-base font-medium transition-all duration-200 
                                    flex-shrink-0 whitespace-nowrap
                                    flex items-center gap-2
                                    border-b-2
                                    ${currentTab === tab.id
                                        ? 'border-emerald-500 text-emerald-600 font-bold bg-emerald-50/50'
                                        : 'border-transparent text-gray-500 hover:text-emerald-600 hover:border-emerald-200 hover:bg-gray-50'
                                    }
                                `}
                            >
                                <IconComponent className="w-5 h-5 flex-shrink-0" />
                                <span>{tab.name}</span>
                            </button>
                        );
                    })}
                </nav>
            </div>
            
            {/* Conteúdo da Aba Ativa */}
            <div className="p-3 sm:p-4 md:p-6 min-h-[50vh] w-full min-w-0 overflow-x-hidden"> 
                <div className="w-full min-w-0">
                    {ActiveComponent && <ActiveComponent pacienteId={pacienteId} />}
                </div>
            </div>
        </div>
    );
};

export default ProntuarioTabs;